/* eslint-disable no-param-reassign */
import axios, {
  AxiosError, AxiosInstance, AxiosPromise, AxiosResponse,
} from 'axios';
import qs from 'qs';
import { v4 } from 'uuid';

import { type IQratorIncidentData } from '@/components/common/antibot-modal/types';
import { CustomEventName } from '@/constants/custom-event-name';
import { logger } from '@/plugins/logger';
import cleanObject from '@/utils/helpers/cleanObject';

import cacheAdapter from './cacheAdapter';
import { ErrorCodesEnum } from './errorCodesBff';

const timeout = 30000;
const layout = 'adaptive';
const locale = 'ru-RU';

interface ICreateFetcherProps {
  baseURL: string;
  getterToken: () => string;
  actionUpdateToken: () => string;
  cookies: Record<string, unknown>;
}

const createFetcher = ({
  baseURL, getterToken, actionUpdateToken, cookies,
}: ICreateFetcherProps) => {
  // если импортим нодой прокидываем урл окружения в baseURL
  const Fetcher = axios.create({
    adapter: cacheAdapter(axios.defaults.adapter),
    baseURL,
    timeout,
    paramsSerializer: (params) => qs.stringify(cleanObject(params)),
  });

  const cookieStr = Object.keys(cookies || {}).map((key) => `${key}=${cookies[key]}`).join(';');

  Fetcher.interceptors.request.use((config) => {
    if (config.headers) {
      config.headers.Authorization = `Bearer ${getterToken()}`;
      config.headers['X-Request-ID'] = v4();

      if (typeof window === 'undefined' && !config.headers?.cookie) {
        config.headers.cookie = cookieStr;
      }
    }
    config.params = config.params || {};
    config.params.layout = config.params.layout || layout;
    config.params.locale = config.params.locale || locale;

    return config;
  }, Promise.reject);

  Fetcher.interceptors.response.use(
    (r: AxiosResponse) => r?.data || {},
    async (e = {}) => {
      const originalRequest = e.config;
      const code = e.response?.data?.code;
      const status = e.response?.status;

      if (code === ErrorCodesEnum.REDIRECT) {
        return Promise.reject({
          status: 302,
          redirect: e.response.data.location,
        });
      }

      // Обработка ответа Qrator, в случае подозрения на бота
      if (e?.response?.status === 403 && !!e?.response?.data?.incidentId) {
        if (typeof document !== 'undefined') {
          // Не SSR
          const incidentData = <IQratorIncidentData>e?.response?.data;

          document.dispatchEvent(new CustomEvent(CustomEventName.IncidentIdError, {
            detail: incidentData,
          }));
        }
      }

      // Устарел токен пытаемся обновить
      if (
        status === 401
        && code === ErrorCodesEnum.UNAUTHORIZED
        && !originalRequest._retry
        && !originalRequest._retrySkip
      ) {
        originalRequest._retry = true;

        try {
          await actionUpdateToken();

          originalRequest.headers.Authorization = `Bearer ${getterToken()}`;

          return await Fetcher(originalRequest);
        } catch (error) {
          const err = error as AxiosError<any>;
          if (err.response?.status === 401 && err.response?.data?.code === 'UNAUTHORIZED' && typeof window !== 'undefined') {
            window.location.assign('/');

            return;
          }
          // eslint-disable-next-line no-param-reassign
          e = err;
        }
      }

      e.error = {};
      const errors = ((e.response || {}).data || {}).errors || (e.response || {}).data || {};
      if (errors) {
        // приведение ошибок бека к общему формату
        e.error = errors[0] || errors;
        logger.error(e.error);
        delete e.response?.data?.errors;
      }
      return Promise.reject(e);
    },
  );

  return Fetcher as FetcherAxiosInstance;
};

export {
  createFetcher,
};

// #region [Типизация интерсептора]

/**
 * Выше интерсептор "Fetcher.interceptors.response.use"
 * меняет ответ и он не соответствует типам которые возвращаются.
 * Тут создаем тип, который обрабатывает этот момент
 */
export type FetcherAxiosInstance = TWrapperForAxiosInterceptor<AxiosInstance> & TNormalizeMethodResponse<AxiosInstance>;

type TWrapperForAxiosInterceptor<T extends AxiosInstance> = {
  [P in keyof TSubType<T, TFetcherMethod>]: TNormalizeMethodResponse<T[P]>;
};

type TNormalizeMethodResponse<M> =
  M extends (...args: any) => AxiosPromise<infer R>
    ? (...args: Parameters<M>) => Promise<R extends unknown ? any : never>
    : M;

// Тип для метода запросов Axios
type TFetcherMethod = <T = any, R = AxiosResponse<T>>(...a: any[]) => Promise<R>;

// Хэлпер для фильтра нужных типов из объектов
type TSubType<Base, Condition> = Pick<
Base,
{
  [Key in keyof Base]: Base[Key] extends Condition ? Key : never;
}[keyof Base]
>;

// #endregion

